import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { DropdownItem, RowSelectionState, M, Button } from 'design-system-web';
import { bulkAssignTaskMemberMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Assignable } from 'domains-work-order-type-contracts';

type MemberSubmittingAction = 'assign' | 'unassignMember';

const TASK_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    assignableUsers: Assignable[];
    refetch?: () => void;
};

export function BulkAssigneeAction({ rowSelection, assignableUsers, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkAssignTaskMember] = useMutation(bulkAssignTaskMemberMutation);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<Assignable | null>(null);
    const [submittingAction, setSubmittingAction] =
        useState<MemberSubmittingAction | null>(null);
    const isSubmitting = submittingAction !== null;

    const selectedTaskIds = useMemo(() => {
        if (!rowSelection) {
            return [];
        }

        return Object.entries(rowSelection)
            .filter(([, isSelected]) => isSelected)
            .map(([taskId]) => taskId);
    }, [rowSelection]);

    const openModal = async () => {
        if (selectedTaskIds.length === 0) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.noSelection')
            );

            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.limitExceeded', {
                    taskSelectionLimit: TASK_SELECTION_LIMIT,
                    selectedTaskCount: selectedTaskIds.length,
                }),
                5000
            );

            return;
        }

        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const submitBulkMember = async (assignee: string | null) => {
        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.limitExceeded', {
                    taskSelectionLimit: TASK_SELECTION_LIMIT,
                    selectedTaskCount: selectedTaskIds.length,
                }),
                5000
            );

            closeModal();

            return;
        }

        const isUnassign = assignee === null;
        const messageKeys = isUnassign
            ? {
                  success:
                      'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.unassignMember.success',
                  error:
                      'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.unassignMember.error',
                  partial:
                      'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.unassignMember.partial',
              }
            : {
                  success:
                      'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.success',
                  error:
                      'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.error',
                  partial:
                      'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.partial',
              };

        setSubmittingAction(isUnassign ? 'unassignMember' : 'assign');

        try {
            const result = await bulkAssignTaskMember({
                variables: {
                    taskIds: selectedTaskIds,
                    assignee,
                },
            });

            const { successCount, failureCount } = result.data?.bulkAssignTaskMember || {
                successCount: 0,
                failureCount: selectedTaskIds.length,
            };

            if (failureCount === 0) {
                await window.Toast.show(
                    t(messageKeys.success, { count: successCount })
                );
            } else if (successCount === 0) {
                await window.Toast.show(t(messageKeys.error));
            } else {
                await window.Toast.show(
                    t(messageKeys.partial, { successCount, failureCount }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            await window.Toast.show(t(messageKeys.error));
        }

        setSubmittingAction(null);
        closeModal();
    };

    const handleConfirm = async () => {
        if (!selectedUser) {
            return;
        }

        await submitBulkMember(selectedUser.value);
    };

    const handleUnassignMember = async () => {
        await submitBulkMember(null);
    };

    return (
        <>
            <Button
                variant="secondary"
                size="large"
                onClick={openModal}
                testId="bulk-task-assignee-select"
            >
                {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.selectMember')}
            </Button>

            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                handleConfirm={handleConfirm}
                title={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.title')}
                confirmText={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.button')}
                cancelText={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.cancel.button')}
                loading={isSubmitting}
                confirmLoading={submittingAction === 'assign'}
                confirmActionDisabled={!selectedUser || isSubmitting}
                cancelActionDisabled={isSubmitting}
                isFullWidth
                footerActionsAfterConfirm={
                    <Button
                        variant="secondary"
                        size="large"
                        onClick={handleUnassignMember}
                        disabled={isSubmitting}
                        loading={submittingAction === 'unassignMember'}
                        testId="bulk-task-assignee-unassign-member"
                    >
                        {t(
                            'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.unassignMember.button'
                        )}
                    </Button>
                }
            >
                <M>
                    {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.modal.prompt', {
                        count: selectedTaskIds.length,
                    })}
                </M>

                <Dropdown
                    isSearchable
                    value={selectedUser?.value || ''}
                    items={assignableUsers || []}
                    onChange={(item: DropdownItem<string>) => {
                        const matchedUser = assignableUsers.find(user => user.value === item.value);

                        if (matchedUser) {
                            setSelectedUser(matchedUser);
                        }
                    }}
                    placeholder={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.selectMember')}
                    doTranslation={false}
                />
            </ConfirmationModal>
        </>
    );
}
